import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { compose, includes } from 'ramda';
import { isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import useValidateFormPromise from '@gef-ui/form-core/hooks/form/useValidateFormPromise';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import Checkbox from '@gef-ui/form-material/Checkbox';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider } from 'components';
import AutocompleteCustomOptionKey from 'components/atoms/GefMui/AutocompleteCustomOptionKey';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import MODALS from 'constants/modals';
import { KCL_DISPLAY_POLICY_LIST_SUBORD } from 'features/common/acl/constants';
import {
	resetCriteria,
	resetOnlyAdvancedSearchForm,
	search,
	setInitialFormValues,
} from 'features/views/policyOverview/actions';
import { beDateKeys, checkboxFields } from 'features/views/policyOverview/constants';
import { FIELDS, POLICY_OVERVIEW_FILTER_FORM_NAME } from 'features/views/policyOverview/form';
import { getHasSubordinates } from 'features/views/user/selectors';
import { fetchContextUserInfo } from 'features/views/userContext/actions';
import { getContextUser, hasContextPermissionById } from 'features/views/userContext/selectors';
import history from 'utils/history';
import { getRoutePath } from 'utils/routeHelper';

import AdvancedSearchModal from './modals/AdvancedSearchModal';
import ProductAutoComplete from './ProductAutoComplete';

const FilterNew = ({ productDomainsCodelist, policyStatusesCodelist, policyPaymentStatusesCodelist }) => {
	const dispatch = useDispatch();
	const next = useControlNext(POLICY_OVERVIEW_FILTER_FORM_NAME);
	const validateFormPromise = useValidateFormPromise();
	const formData = useFormData();
	const formIsInitiated = isObject(formData);

	const contextUser = useSelector(getContextUser);

	useEffect(() => {
		if (contextUser) {
			dispatch(fetchContextUserInfo());
		}
	}, [dispatch, contextUser]);

	const userHasSubordinates = useSelector(getHasSubordinates);
	const hasSubordinates = contextUser ? contextUser?.hasSubordinates : userHasSubordinates;

	const userDisplaySubOrdPolicyListPerm = useSelector((state) =>
		hasPermissionByIds(state)(KCL_DISPLAY_POLICY_LIST_SUBORD),
	);
	const contextDisplaySubOrdPolicyListPerm = useSelector((state) =>
		hasContextPermissionById(state, KCL_DISPLAY_POLICY_LIST_SUBORD),
	);
	const hasDisplaySubOrdPolicyList = contextUser
		? contextDisplaySubOrdPolicyListPerm
		: userDisplaySubOrdPolicyListPerm;

	const showWithSubordinatesCheckbox = hasDisplaySubOrdPolicyList && hasSubordinates;

	const handleSubmit = useCallback(() => {
		dispatch(search());
	}, [dispatch]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleSubmit });

	const checkPoliciesUrlForPassedQueryParamsAndPossiblyPrefillSearchForm = useCallback(async () => {
		const searchParams = new URLSearchParams(window.location.search);
		const searchParamsArr = Array.from(searchParams.keys());

		if (searchParamsArr.length) {
			searchParamsArr.forEach((key) => {
				const value = searchParams.get(key);
				let finalValue;

				if (value) {
					if (includes(key, beDateKeys)) {
						finalValue = new Date(value);
					} else if (includes(key, checkboxFields)) {
						finalValue = value === 'true';
					} else {
						finalValue = value;
					}
					return next(setValue({ fieldName: key, value: finalValue }));
				}
			});

			try {
				await validateFormPromise();
				dispatch(search());
			} catch (e) {
				handleSubmitWrongPolicyNoCDV(null, e);
			}

			history.replace(getRoutePath('policyOverview'));
		}
	}, [dispatch, next, validateFormPromise, handleSubmitWrongPolicyNoCDV]);

	useEffect(() => {
		if (formIsInitiated) {
			dispatch(setInitialFormValues());
			checkPoliciesUrlForPassedQueryParamsAndPossiblyPrefillSearchForm();
		}
	}, [next, dispatch, formIsInitiated, checkPoliciesUrlForPassedQueryParamsAndPossiblyPrefillSearchForm]);

	const handleReset = useCallback(() => {
		dispatch(resetCriteria());
	}, [dispatch]);

	const handleExit = useCallback(() => {
		dispatch(resetOnlyAdvancedSearchForm());
	}, [dispatch]);

	const resetProductAutoComplete = useCallback(() => {
		next(setValue({ fieldName: FIELDS.product, value: null }));
	}, [next]);

	return (
		<Grid container>
			<Grid size={12}>
				<AdvancedSearchModal id={MODALS.policyOverview_advanced_search} onExit={handleExit} />
				<Grid container spacing={2}>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('policyOverview:firstName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('policyOverview:surname')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={6}>
						<TextField
							size="small"
							fieldName={FIELDS.businessName}
							label={getLabelMessage('policyOverview:businessName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.BUSINESS_NAME,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.businessIn}
								label={getLabelMessage('policyOverview:businessIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.TAX_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:policyNo')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.policyNumber}
								label={getLabelMessage('policyOverview:policyNumber')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.POLICY_NO,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={10}>
						<Grid container spacing={2}>
							<Grid size={4}>
								<AutocompleteCustomOptionKey
									size="small"
									fieldName={FIELDS.policyStatus}
									label={getLabelMessage('policyOverview:policyStatus')}
									options={policyStatusesCodelist}
									labelPropName="description"
									keyPropName="id"
								/>
							</Grid>
							<Grid size={4}>
								<Autocomplete
									size="small"
									fieldName={FIELDS.productDomain}
									label={getLabelMessage('policyOverview:productDomain')}
									options={productDomainsCodelist}
									labelPropName="description"
									onChange={resetProductAutoComplete}
								/>
							</Grid>
							<Grid size={4}>
								<ProductAutoComplete />
							</Grid>
						</Grid>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.policyValueDateAnnualFrom}
							label={getLabelMessage('policyOverview:policyValueDateAnnualFrom')}
							placeholder={getLabelMessage('dateFormats:ddmm')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.DAY_MONTH_DATE,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.policyValueDateAnnualTo}
							label={getLabelMessage('policyOverview:policyValueDateAnnualTo')}
							placeholder={getLabelMessage('dateFormats:ddmm')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.DAY_MONTH_DATE,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.policyOpenDateAnnualFrom}
							label={getLabelMessage('policyOverview:policyOpenDateAnnualFrom')}
							placeholder={getLabelMessage('dateFormats:ddmm')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.DAY_MONTH_DATE,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.policyOpenDateAnnualTo}
							label={getLabelMessage('policyOverview:policyOpenDateAnnualTo')}
							placeholder={getLabelMessage('dateFormats:ddmm')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.DAY_MONTH_DATE,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.policyPaymentStatus}
							label={getLabelMessage('policyOverview:policyPaymentStatus')}
							options={policyPaymentStatusesCodelist}
							labelPropName="shortDescription"
						/>
					</Grid>
					<Grid size={9}>
						<Grid container spacing={2}>
							<Grid size={2}>
								<Checkbox
									defaultValue
									fieldName={FIELDS.gcpContractsOnly}
									label={getLabelMessage('policyOverview:gcpContractsOnly')}
								/>
							</Grid>
							<Grid size={2}>
								<Checkbox
									defaultValue
									fieldName={FIELDS.gpContractsOnly}
									label={getLabelMessage('policyOverview:gpContractsOnly')}
								/>
							</Grid>
							<Grid size={2.5}>
								<Checkbox
									defaultValue={false}
									fieldName={FIELDS.frameworkPolicyOnly}
									label={getLabelMessage('policyOverview:frameworkPolicyOnly')}
								/>
							</Grid>
							<Tooltip title={getLabelMessage('tooltips:activePolicyWoQuotationOnly')}>
								<Grid size={2.5}>
									<Checkbox
										defaultValue={false}
										fieldName={FIELDS.activePolicyWoQuotationOnly}
										label={getLabelMessage('policyOverview:activePolicyWoQuotationOnly')}
									/>
								</Grid>
							</Tooltip>
							{showWithSubordinatesCheckbox && (
								<Grid size={3}>
									<Checkbox
										defaultValue={false}
										fieldName={FIELDS.withSubordinates}
										label={getLabelMessage('policyOverview:withSubordinates')}
									/>
								</Grid>
							)}
						</Grid>
					</Grid>
					<Grid size={8} />
					<Grid size={2}>
						<Button variant="outlined" onClick={handleReset} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={2}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							onClickError={handleSubmitWrongPolicyNoCDV}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

FilterNew.propTypes = {
	policyPaymentStatusesCodelist: PropTypes.array,
	policyStatusesCodelist: PropTypes.array,
	productDomainsCodelist: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.productDomains, 'productDomainsCodelist', true),
	codelistProvider(codelists.policyStatuses, 'policyStatusesCodelist', true),
	codelistProvider(codelists.policyPaymentStatuses, 'policyPaymentStatusesCodelist', true),
)(FilterNew);
